import { createFeatureSelector, createSelector } from '@ngrx/store';
import { TREQUEST_RoamingQcTransactions, TRoamingQcEvent, TRoamingQcStore } from '@monorepo/types';
import {
  getAllStationIds,
  getBctxConfig,
  getBuzKeys,
  getHSKValue,
  getSubjectKey,
  selectedSubject,
} from '../app-master-db/app-master.query';
import { selectAllShifts, selectedShift } from '../shifts/shifts.query';
import { getEventsArray, getSelectedStationIdAndShiftId } from '../input-app-client-db/input-app-client-db.query';
import { EventUtil } from '@monorepo/utils';

export const selectRoamingQcStore = createFeatureSelector<TRoamingQcStore>('ROAMING_QC_STORE');
export const getRoamingQcLockDetailsAndNextCacheResetTimestamp = createSelector(
  selectRoamingQcStore,
  (roamingQcStore) => {
    return {
      lockDetails: roamingQcStore.lockDetails,
      nextCacheResetTimestamp: roamingQcStore.roamingQcDB.nextCacheResetTimestamp,
    };
  },
);

export const getTransactionLogRecords = createSelector(selectRoamingQcStore, (roamingQcStore) => {
  return roamingQcStore.transactions;
});

export const getEpfDetails = createSelector(
  selectRoamingQcStore,
  selectedSubject,
  selectedShift,
  (store, subject, shift) => {
    if (
      subject?.subject &&
      shift?.id &&
      store.roamingQcDB.persistanceCache.epfDetails.lineMap[subject.subject] &&
      store.roamingQcDB.persistanceCache.epfDetails.lineMap[subject.subject].shiftMap[shift.id]
    ) {
      return store.roamingQcDB.persistanceCache.epfDetails.lineMap[subject.subject].shiftMap[shift.id];
    } else return [];
  },
);

export const getRecentCategoryList = createSelector(selectRoamingQcStore, (store) => {
  return store.roamingQcDB.dailyCache.recentCategoryList;
});

export const getTransactionsPayloadMetadata = createSelector(
  getSubjectKey,
  selectedSubject,
  getHSKValue,
  getBuzKeys,
  (subjectKey, station, hsk, buzzKeys) => {
    const payload: TREQUEST_RoamingQcTransactions = {
      subject_key: subjectKey,
      compressed: false,
      columns: buzzKeys,
      dates: [],
      window: '',
      subject: station ? station.subject : '',
      hsk,
    };
    return payload;
  },
);

export const getInProgressDetails = createSelector(
  selectRoamingQcStore,
  selectedSubject,
  selectedShift,
  (store, subject, shift) => {
    if (
      subject?.subject &&
      shift?.id &&
      store.roamingQcDB.dailyCache.inProgressDetails.lineMap[subject.subject] &&
      store.roamingQcDB.dailyCache.inProgressDetails.lineMap[subject.subject].shiftMap[shift.id]
    ) {
      return store.roamingQcDB.dailyCache.inProgressDetails.lineMap[subject.subject].shiftMap[shift.id];
    } else return null;
  },
);
export const getInitialRoundInfo = createSelector(
  selectRoamingQcStore,
  getSelectedStationIdAndShiftId,
  (store, { shiftId, stationId }) => {
    return store.roamingQcDB.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId]
      ? store.roamingQcDB.dailyCache.lineShiftWiseRoundInfo.lineMap[stationId].shiftMap[shiftId]
      : undefined;
  },
);
export const getInitialRoundInfoAndInProgressDetails = createSelector(
  getInitialRoundInfo,
  getInProgressDetails,
  (initialRoundInfo, inProgressDetails) => {
    return { inProgressDetails, initialRoundInfo };
  },
);
export const getCurrentRoundInfo = createSelector(
  getEventsArray,
  getSelectedStationIdAndShiftId,
  getInProgressDetails,
  getInitialRoundInfo,
  (events, selectedStationIdAndShiftId, inProgressDetails, initialRoundInfo) => {
    const roamingQcEvents: TRoamingQcEvent[] = events.filter(
      (item) =>
        item.type === 'ROAMING_QC_EVENT' &&
        EventUtil.hasEventHappenedInStationDetails(
          item,
          selectedStationIdAndShiftId.stationId,
          selectedStationIdAndShiftId.shiftId,
        ),
    ) as TRoamingQcEvent[];

    const lastEventRound =
      roamingQcEvents.length > 0
        ? roamingQcEvents[roamingQcEvents.length - 1].events[0].eventInfo.auditDetails.round
        : initialRoundInfo
        ? initialRoundInfo
        : 0;
    const currentRound = inProgressDetails ? inProgressDetails.round : lastEventRound + 1;
    const filteredRoamingQcEvents = roamingQcEvents.filter((event) => {
      return event.events[0].eventInfo.auditDetails.round === currentRound;
    });
    return {
      inProgressDetails: inProgressDetails,
      operations: Object.values(
        filteredRoamingQcEvents.reduce((acc, curr) => {
          curr.events.forEach((event) => {
            if (!acc[event.eventInfo.auditDetails.operation])
              acc[event.eventInfo.auditDetails.operation] = event.eventInfo.auditDetails.operation;
          });
          return acc;
        }, {} as Record<string, string>),
      ),
      operators: Object.values(
        filteredRoamingQcEvents.reduce((acc, curr) => {
          curr.events.forEach((event) => {
            if (!acc[event.eventInfo.auditDetails.operator])
              acc[event.eventInfo.auditDetails.operator] = event.eventInfo.auditDetails.operator;
          });
          return acc;
        }, {} as Record<string, string>),
      ),
      currentRound,
      completedAudits: filteredRoamingQcEvents,
    };
  },
);
export const getAllStationIdsAndShifts = createSelector(
  getAllStationIds,
  selectAllShifts,
  (allStaionIds, allShifts) => {
    return {
      allStaionIds,
      allShifts,
    };
  },
);
